import React, { useEffect, useState, useMemo, useCallback } from "react";
import styles from "./ComponentsStyles.module.css";
import { FaCircle } from "react-icons/fa6";
import { Mail, Maximize, Minimize, ZoomIn, ZoomOut, RefreshCw, MoreVertical } from "lucide-react"; 
import { useNavigate } from "react-router-dom";
import axios, { API_BASE_URL } from "../services/axiosInstance";
import { useSessionSocket, useOnlineUsers, useSocketStatus } from "../pages/contexts/SocketContext";
import { decodeToken } from "../utils/auth";

import DockMenu from "./DockMenu"; // Importando o DockMenu para renderizar na foto

// Recebemos onRefresh e isRefreshing via props agora
export default function UserInfo({ onRefresh, isRefreshing }) {
  const [info, setInfo] = useState(null);
  const [tempoRestante, setTempoRestante] = useState("");
  const [expirando, setExpirando] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  
  // Estados das Ferramentas
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const [mostrarMenuMobile, setMostrarMenuMobile] = useState(false);
  
  const [navegadorOnline, setNavegadorOnline] = useState(navigator.onLine);

  const navigate = useNavigate();
  const sessionSocket = useSessionSocket();
  const onlineUsers = useOnlineUsers();
  const { connected: socketConectado } = useSocketStatus(); 

  // --- LÓGICA DE FERRAMENTAS ---
  useEffect(() => {
      document.documentElement.style.zoom = zoomLevel;
  }, [zoomLevel]);

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));

  const toggleFullscreen = () => { 
      if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch((err) => console.warn(err));
          setIsFullscreen(true);
      } else {
          document.exitFullscreen();
          setIsFullscreen(false);
      }
  };

  useEffect(() => {
      const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleRefreshClick = () => {
      if (onRefresh) onRefresh();
      else window.location.reload();
  };
  // ------------------------------

  // 1. Monitoramento de Rede
  useEffect(() => {
    const handleOnline = () => setNavegadorOnline(true);
    const handleOffline = () => setNavegadorOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 2. Decodifica token
  useEffect(() => {
    const payload = decodeToken();
    if (payload) {
      setInfo({ 
        alias: payload.alias || "Usuário", 
        expiresAt: payload.exp * 1000, 
        matricula: payload.matricula,
        id: payload.id,
        foto: payload.foto 
      });
    }
  }, []);

  // 3. Contagem de Mensagens
  const atualizarContagem = useCallback(async () => {
    try {
      if(!info?.id) return;
      const { data } = await axios.get("/mensagens/nao-lidas/contagem");
      setNaoLidas(data.naoLidas);
    } catch (error) { console.error(error); }
  }, [info?.id]);

  useEffect(() => {
    if (info?.id) atualizarContagem();
  }, [info?.id, atualizarContagem]);

  // 4. Listener de Nova Mensagem
  useEffect(() => {
    if (!sessionSocket || !info?.id) return;
    const handleNovaMensagem = (payload) => {
      const idsDestino = payload.usuariosId || [];
      if (idsDestino.map(Number).includes(Number(info.id))) {
        atualizarContagem();
      }
    };
    sessionSocket.on("nova_mensagem", handleNovaMensagem);
    return () => sessionSocket.off("nova_mensagem", handleNovaMensagem);
  }, [sessionSocket, info?.id, atualizarContagem]);

  // 5. Status Online
  const isTrulyOnline = useMemo(() => {
    if (!navegadorOnline) return false;
    if (!socketConectado) return false;
    if (!info?.matricula) return false;
    return onlineUsers.some((user) => user.matricula === info.matricula);
  }, [navegadorOnline, socketConectado, onlineUsers, info]);

  // 6. Timer de Expiração
  useEffect(() => {
    if (!info?.expiresAt) return;
    const atualizarTempo = () => {
      const diff = info.expiresAt - Date.now();
      if (diff <= 0) {
        setTempoRestante("expirado"); setExpirando(true);
      } else {
        const totalMin = Math.floor(diff / 1000 / 60);
        const horas = Math.floor(totalMin / 60);
        const minutos = totalMin % 60;
        setTempoRestante(horas > 0 ? `${horas}h ${minutos}m` : `${minutos}min`);
        setExpirando(diff <= 2 * 60 * 1000);
      }
    };
    atualizarTempo();
    const interval = setInterval(atualizarTempo, 60000);
    return () => clearInterval(interval);
  }, [info]);

  const handleIrParaMensagens = () => {
    navigate({ pathname: '/plenario', search: '?tab=mensagens' });
  };

  if (!info) return <div style={{ padding: "0.5rem" }}>...</div>;

  const fotoUrl = info.foto ? `${API_BASE_URL}/images/usuarios/${info.foto}` : null;

  return (
    <>
      <div className={styles.usuarioLogado} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        
        {/* GRUPO DE FERRAMENTAS (Zoom, Fullscreen, Refresh) */}
        <div className={styles.containerFerramentas} style={{ display: "flex", alignItems: "center", position: "relative", width: 'auto'}}>
            
            {/* Visível apenas em Telas Maiores (Controlado via CSS) */}
            <div className={styles.ferramentasDesktop} style={{ display: "flex", gap: "8px" }}>
                <button onClick={handleZoomOut} title="Diminuir Texto" className={styles.btnFerramenta}>
                    <ZoomOut size={20} color="#fff" />
                </button>
                <button onClick={handleZoomIn} title="Aumentar Texto" className={styles.btnFerramenta}>
                    <ZoomIn size={20} color="#fff" />
                </button>
                <button onClick={toggleFullscreen} title={isFullscreen ? "Sair Tela Cheia" : "Tela Cheia"} className={styles.btnFerramenta}>
                    {isFullscreen ? <Minimize size={20} color="#fff" /> : <Maximize size={20} color="#fff" />}
                </button>
                <button onClick={handleRefreshClick} title="Sincronizar" disabled={isRefreshing} className={styles.btnFerramenta} style={{ opacity: isRefreshing ? 0.5 : 1 }}>
                    <RefreshCw size={20} color="#fff" className={isRefreshing ? styles.spinner : ''} />
                </button>
            </div>

            {/* Visível apenas no Mobile */}
            <div className={styles.ferramentasMobile}>
                <button style={{padding: 5}}
                    onClick={() => setMostrarMenuMobile(!mostrarMenuMobile)} 
                    className={styles.btnFerramenta}
                >
                    <MoreVertical size={20} color="#fff" />
                </button>

                {mostrarMenuMobile && (
                    <div className={styles.menuFerramentasDropdown}>
                        <button onClick={handleZoomOut}>Diminuir Texto</button>
                        <button onClick={handleZoomIn}>Aumentar Texto</button>
                        <button onClick={toggleFullscreen}>{isFullscreen ? "Sair Tela Cheia" : "Tela Cheia"}</button>
                        <button onClick={handleRefreshClick} disabled={isRefreshing}>Sincronizar</button>
                    </div>
                )}
            </div>
        </div>

        {/* Separador */}
        <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.3)' }}></div>

        {/* Ícone de Mensagens */}
        <div 
          style={{ position: 'relative', cursor: 'pointer', display: 'flex', marginRight: 5 }} 
          onClick={handleIrParaMensagens} 
          title="Ver mensagens"
        >
          <Mail style={{color: '#fff'}} /> 
          {naoLidas > 0 && (
            <span style={{
              position: 'absolute', top: -6, right: -6, background: '#ff0000',color: '#fff', 
              borderRadius: '50%', width: '16px', height: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
              border: '1px solid #fff'
            }}>
              {naoLidas > 9 ? '9+' : naoLidas}
            </span>
          )}
        </div>

        {/* Info Texto */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right", width: '100%', gap: 5 }}>
          <strong style={{gap: 5, display: 'flex', alignItems: 'center'}}>
            <FaCircle 
              size={10} 
              style={{width: "0.7rem", height: "0.7rem"}} 
              color={isTrulyOnline ? "#39e015" : "#ff0000"} 
              title={isTrulyOnline ? "Conectado" : "Offline / Sem conexão"}
            />
            {info.alias}
          </strong>
          <small className={styles.hideMobile} style={{ color: expirando ? "#ff9999" : "#fff" }}>
            {tempoRestante === "expirado" ? "Expirada" : `Expira em ${tempoRestante}`}
          </small>
        </div>

        {/* DockMenu atua como Foto do Usuário agora */}
        <DockMenu fotoUrl={fotoUrl} />
      </div>
    </>
  );
}